import { useEffect, useState } from 'react'
import type { Api } from '../api.js'
import { parseInvite } from '../invite.js'
import { useT } from './i18n-context.js'

/**
 * Экран входа (D29): с чего начинается приложение без параметров.
 *
 * Путей два, и они не равнозначны по стоимости ошибки. «Создать
 * персонажа» — драфт: ничего не требует и ничего не ломает, если
 * передумать. «Присоединиться» требует ссылку от мастера, и если
 * ссылка не та, человек попадёт не в свою партию — поэтому ссылка
 * разбирается до перехода, а не после.
 *
 * Поля для кода здесь больше нет (D55). Оно стояло запертым с фазы 1
 * с оговоркой «заработает вместе с кампаниями»; кампании появились,
 * а входом код так и не стал: за стол садятся по ссылке, а код нужен
 * только тому, чей драфт остался на другом устройстве, — и вводит
 * он его на экране приглашения, где заявка и подаётся.
 *
 * Заход по ссылке с параметрами этот экран минует: там уже всё
 * известно, и лишний экран между ссылкой и списком — лишний шаг.
 */
export function StartScreen({
  api,
  onCreate,
  onJoin,
  onList,
}: {
  readonly api: Api
  readonly onCreate: () => void
  /** Приглашение разобрано: дальше экран приглашения по токену (D55). */
  readonly onJoin: (token: string) => void
  readonly onList: () => void
}) {
  const t = useT()
  const [link, setLink] = useState('')
  const [refusal, setRefusal] = useState<string | null>(null)
  // Сколько драфтов уже есть у этого устройства. При первом заходе
  // их ноль, и экран показывает ровно два пути; у вернувшегося
  // появляется третий — к своим персонажам, иначе они достижимы
  // только по прямой ссылке.
  const [characters, setCharacters] = useState(0)

  useEffect(() => {
    let cancelled = false
    void api
      .listCharacters(null)
      .then((list) => {
        if (!cancelled) setCharacters(list.length)
      })
      .catch(() => {
        // Список не открылся — экран входа от этого не ломается:
        // оба пути с него работают и без него.
      })
    return () => {
      cancelled = true
    }
  }, [api])

  const join = () => {
    const parsed = parseInvite(link)
    if (!parsed.ok) {
      setRefusal(t(`start.refusal.${parsed.reason}`))
      return
    }
    setRefusal(null)
    onJoin(parsed.token)
  }

  return (
    <section className="app-screen" aria-labelledby="start-title">
      <h2 id="start-title">{t('start.title')}</h2>

      <section className="app-slot" aria-labelledby="start-create">
        <h3 id="start-create">{t('start.createTitle')}</h3>
        <p className="app-slot-hint">{t('start.createText')}</p>
        <div className="app-actions">
          <button type="button" className="app-button" onClick={onCreate}>
            {t('start.create')}
          </button>
        </div>
      </section>

      <section className="app-slot" aria-labelledby="start-join">
        <h3 id="start-join">{t('start.joinTitle')}</h3>
        <p className="app-slot-hint">{t('start.joinText')}</p>
        <form
          className="app-form"
          onSubmit={(event) => {
            event.preventDefault()
            join()
          }}
        >
          <div data-slot="invite" data-invalid={refusal === null ? undefined : 'true'}>
            <label className="app-field">
              <span>{t('start.link')}</span>
              <input
                type="text"
                inputMode="url"
                value={link}
                aria-invalid={refusal === null ? undefined : 'true'}
                aria-describedby={refusal === null ? undefined : 'invite-error'}
                onChange={(event) => {
                  setLink(event.target.value)
                  // Ошибка снимается правкой поля, а не повторной
                  // отправкой: то же правило, что в визарде.
                  setRefusal(null)
                }}
              />
            </label>
            {refusal !== null && (
              <p className="app-problems" id="invite-error" role="alert">
                {refusal}
              </p>
            )}
          </div>
          <div className="app-actions">
            <button type="submit" className="app-button app-button-quiet">
              {t('start.join')}
            </button>
          </div>
        </form>
      </section>

      {characters > 0 && (
        <div className="app-actions">
          <button
            type="button"
            className="app-button app-button-quiet"
            onClick={onList}
          >
            {t('start.mine', { count: characters })}
          </button>
        </div>
      )}
    </section>
  )
}
